import { customInputProps } from '@/app/entrar/customInputProps';
import {
  FloatingLabelPasswordInput,
  FloatingLabelTextInput,
} from '@/components/FloatingLabelComponents/FloatingLabelInput';
import useApi from '@/hooks/useApi/useApi';
import { shouldSkipLoggingForAuthError } from '@/utils/error';
import { Alert, Anchor, Button, Flex, Group, PinInput, Text, Title } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import { IconArrowLeft } from '@tabler/icons-react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { ReactNode, useState } from 'react';

type SendNewPasswordForm = {
  email: string;
  password: string;
  confirmPassword: string;
  verificationCode?: number;
};

export default function NewPassword() {
  const searchParams = useSearchParams();
  const emailFromUrl = searchParams?.get('email') || '';
  const form = useForm<SendNewPasswordForm>({
    initialValues: {
      email: emailFromUrl,
      password: '',
      confirmPassword: '',
      verificationCode: undefined,
    },
    validate: {
      email: isNotEmpty('E-mail é obrigatório'),
      password: isNotEmpty('Insira uma senha'),
      confirmPassword: (value, values) =>
        value === values.password ? undefined : 'As senhas precisam ser iguais',
      verificationCode: (value) => {
        if (!/^\d*$/.test(String(value))) {
          return 'O código de verificação deve conter apenas números';
        }
        if (String(value).length !== 6) {
          return 'O código de verificação deve ter 6 dígitos';
        }
        return null;
      },
    },
    validateInputOnBlur: true,
  });

  const router = useRouter();

  const { put, loading } = useApi<{ email: string; password: string; verificationCode: number }>(
    'createPassword',
  );

  const [feedbackNode, setFeedbackNode] = useState<ReactNode>();

  const handleSubmit = async ({ password, ...values }: SendNewPasswordForm) => {
    setFeedbackNode(undefined);
    put<{ token: string }>(
      { password, email: values.email, verificationCode: values.verificationCode ?? 0 },
      {
        error: {
          message: 'Ocorreu um erro ao criar a senha. Tente novamente',
          showNotification: false,
          shouldSkipLogging: shouldSkipLoggingForAuthError,
        },
        onSuccess: () => {
          router.push(`/entrar?email=${encodeURIComponent(form.values.email)}`);
        },
        onError: (error) => {
          if (error?.message.includes('User not found')) {
            setFeedbackNode(
              <Alert
                color="red"
                radius="md"
                p="sm"
                classNames={{ message: 'text-center text-white' }}
              >
                Usuário não encontrado. Entre em contato com o administrador do sistema
              </Alert>,
            );
          } else if (error?.message.includes('Invalid or expired verification code')) {
            setFeedbackNode(
              <Alert
                color="red"
                radius="md"
                p="sm"
                classNames={{ message: 'text-center text-white' }}
              >
                Código de verificação inválido ou expirado. Verifique seu e-mail e tente novamente
              </Alert>,
            );
          } else if (error?.message.includes('User already has a password')) {
            setFeedbackNode(
              <Alert
                color="red"
                radius="md"
                p="sm"
                classNames={{ message: 'text-center text-white' }}
              >
                <Text c="light">
                  Você já se registrou anteriormente. Clique{' '}
                  <Anchor
                    component="button"
                    c="red"
                    fw="bold"
                    onClick={() =>
                      router.push(`/entrar?email=${encodeURIComponent(form.values.email)}`)
                    }
                  >
                    aqui
                  </Anchor>{' '}
                  para entrar
                </Text>
              </Alert>,
            );
          } else {
            setFeedbackNode(
              <Alert
                color="red"
                radius="md"
                p="sm"
                classNames={{ message: 'text-center text-white' }}
              >
                Código de verificação inválido ou expirado. Verifique seu e-mail e tente novamente
              </Alert>,
            );
          }
        },
      },
    );
  };

  return (
    <form className="flex h-full flex-col text-white" onSubmit={form.onSubmit(handleSubmit)}>
      <div>
        <Title order={1} ta="center" mb="sm">
          Recuperar senha
        </Title>
        <Text ta="center" mb="lg">
          Informe seu e-mail e o código de verificação enviado para recuperar sua senha. Em seguida,
          crie uma nova senha.
        </Text>
      </div>
      <div className="flex flex-col gap-4 animate-[fade-in_0.5s_ease-in]">
        <FloatingLabelTextInput
          {...customInputProps}
          {...form.getInputProps('email')}
          type="email"
          label="E-mail"
          autoComplete="on"
        />

        <FloatingLabelPasswordInput
          {...customInputProps}
          {...form.getInputProps('password')}
          label="Crie uma senha"
        />

        <FloatingLabelPasswordInput
          {...customInputProps}
          {...form.getInputProps('confirmPassword')}
          label="Confirmar senha"
        />

        <Text ta="left" size="sm" fw={500}>
          Código de verificação
        </Text>
        <Flex align="center" direction="column" mt="sm">
          <PinInput
            {...form.getInputProps('verificationCode')}
            type="number"
            length={6}
            classNames={{ input: '!text-light-600 bg-dark-900 bg-opacity-20' }}
          />
        </Flex>

        {feedbackNode}

        <Group justify="space-between" align="center" mt="md">
          <Button
            component={Link}
            variant="subtle"
            color="white"
            leftSection={<IconArrowLeft size={18} stroke={1.5} />}
            href="/registrar-senha"
          >
            Voltar
          </Button>

          <Button type="submit" variant="filled" loading={loading}>
            Recuperar Conta
          </Button>
        </Group>
      </div>
    </form>
  );
}
